import { create } from "zustand";
import type { Address } from "../schemas/paycode/address";
import type { Card } from "../schemas/paycode/card";
import type { CardToken } from "../schemas/paycode/card-token";
import type { Client } from "../schemas/paycode/client";
import type { DynamicField } from "../schemas/paycode/dynamic-field";
import type { Link } from "../schemas/paycode/link";
import type { LinkConfig } from "../schemas/paycode/link-config";
import type {
  PaymentMethodKey,
  PaymentMethods,
} from "../schemas/paycode/payment-methods";
import type { Product } from "../schemas/paycode/product";

export type StepId = "customer" | "shipping" | "billing" | "payment";

export type StepStatus = "pending" | "completed";

interface CheckoutStore {
  hash: string;
  business?: string;
  client?: Client;
  shippingAddress?: Address;
  billingAddress?: Address;
  shippingAddressOptions?: Address[];
  billingAddressOptions?: Address[];
  dynamicFields?: DynamicField[];
  products: Product[];
  total: number;
  concept: string;
  redirectUrl?: string;
  card?: Card;
  paymentMethods?: PaymentMethods;
  selectedPaymentMethod?: PaymentMethodKey;
  cardTokens?: CardToken[];
  tokenizable?: boolean;
  termsUrl?: string;
  linkType?: string;
  config?: LinkConfig;

  lastRejection?: string;

  editProductsOpen: boolean;

  steps: StepId[];
  currentStep: StepId;
  stepStatus: Record<StepId, StepStatus>;

  loadLink(link: Link): void;
  setClient(client: Client): void;
  setShipping(address: Address): void;
  setBilling(address: Address): void;
  setDynamicFields(dynamics: DynamicField[]): void;
  setProducts(products: Product[]): void;
  setEditProductsOpen(open: boolean): void;
  setCard(card: Card): void;
  setSelectedPaymentMethod(method: PaymentMethodKey): void;
  setLastRejection(reason?: string): void;
  setCurrentStep(id: StepId): void;
  completeCurrentStep(): void;
}

export const useCheckoutStore = create<CheckoutStore>((set) => ({
  hash: "",
  total: 0,
  products: [],
  concept: "",
  editProductsOpen: false,

  steps: [],
  currentStep: "payment",
  stepStatus: {
    payment: "pending",
    customer: "pending",
    shipping: "pending",
    billing: "pending",
  },

  loadLink: (link: Link) => {
    set(() => {
      const total = calculateTotalFromProducts(link.products);
      const steps = buildSteps(link);
      return {
        total,
        products: link.products,
        hash: link.hash,
        business: link.business,
        client:
          link.requiresCustomerInfo || link.linkType === "permanent"
            ? undefined
            : link.client || undefined,
        shippingAddressOptions: link.shippingAddressOptions ?? undefined,
        billingAddressOptions: link.billingAddressOptions ?? undefined,
        dynamicFields: link.dynamicFields,
        paymentMethods: link.paymentMethods,
        selectedPaymentMethod: link.initialPaymentMethods,
        lastRejection: undefined,
        steps,
        currentStep: steps[0],
        cardTokens: link.client?.tokens,
        tokenizable: link.tokenizable ?? false,
        concept: link.concept,
        redirectUrl: link.redirectUrl ?? undefined,
        termsUrl: link.termsUrl,
        linkType: link.linkType,
        config: link.linkConfig,
      };
    });
  },

  setClient: (client: Client) => {
    set(() => ({ client }));
  },

  setShipping: (shippingAddress: Address) => {
    set(() => ({ shippingAddress }));
  },

  setBilling: (billingAddress: Address) => {
    set(() => ({ billingAddress }));
  },

  setDynamicFields: (dynamicFields: DynamicField[]) => {
    set(() => ({ dynamicFields }));
  },

  setProducts: (products: Product[]) => {
    set(() => ({ products, total: calculateTotalFromProducts(products) }));
  },

  setEditProductsOpen: (editProductsOpen: boolean) => {
    set(() => ({ editProductsOpen }));
  },

  setCard: (card: Card) => {
    set(() => ({ card }));
  },

  setSelectedPaymentMethod: (selectedPaymentMethod: PaymentMethodKey) => {
    // The rejection belongs to the attempt that produced it, so it must not
    // survive a switch to another payment method.
    set(() => ({ selectedPaymentMethod, lastRejection: undefined }));
  },

  setLastRejection: (lastRejection?: string) => {
    set(() => ({ lastRejection }));
  },

  setCurrentStep: (id: StepId) => {
    set(() => ({ currentStep: id, lastRejection: undefined }));
  },

  completeCurrentStep: () => {
    set((state) => {
      const index = state.steps.indexOf(state.currentStep);
      const nextStep =
        index !== state.steps.length - 1
          ? state.steps[index + 1]
          : state.currentStep;

      return {
        stepStatus: { ...state.stepStatus, [state.currentStep]: "completed" },
        currentStep: nextStep,
        lastRejection: undefined,
      };
    });
  },
}));

export const useClient = () => useCheckoutStore((s) => s.client);
export const useShippingAddress = () =>
  useCheckoutStore((s) => s.shippingAddress);
export const useBillingAddress = () =>
  useCheckoutStore((s) => s.billingAddress);
export const useDynamicFields = () => useCheckoutStore((s) => s.dynamicFields);
export const useCard = () => useCheckoutStore((s) => s.card);
export const usePaymentMethods = () =>
  useCheckoutStore((s) => s.paymentMethods);
export const useSelectedPaymentMethod = () =>
  useCheckoutStore((s) => s.selectedPaymentMethod);
export const useSetSelectedPaymentMethod = () =>
  useCheckoutStore((s) => s.setSelectedPaymentMethod);

const calculateTotalFromProducts = (products: Product[]) =>
  Number.parseFloat(
    products
      .reduce<number>(
        (accumulated, product) =>
          accumulated + product.amount * product.quantity,
        0,
      )
      .toFixed(2),
  );

export const buildSteps = (link: Link): StepId[] => {
  const steps: StepId[] = [];
  if (link.askForShipping) steps.push("shipping");
  if (link.askForBilling) steps.push("billing");
  if (link.requiresCustomerInfo) steps.push("customer");
  steps.push("payment");
  return steps;
};
